import MppGanttViewer from '@/components/features/Mpp/MppGanttViewer';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { useTranslations } from '@/hooks/useTranslations';
import IndexLayout from '@/layouts/index-layout';
import { mppToGantt, type MppSchedule } from '@/lib/gantt/mppTransform';
import { type BreadcrumbItem, type SharedData } from '@/types';
import type { MppFileSummary } from '@/types/mpp';
import { Head, router, usePage } from '@inertiajs/react';
import { AlertCircle, ArrowLeft, Download, GanttChartSquare, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { route } from 'ziggy-js';

interface MppShowProps {
    file: MppFileSummary;
    gantt: MppSchedule | null;
}

export default function MppShow({ file, gantt }: MppShowProps) {
    const { t } = useTranslations();
    const { auth } = usePage<SharedData>().props;
    const canDelete = (auth?.abilities ?? []).includes('mpp.delete');

    const isPending = file.status === 'queued' || file.status === 'processing';

    // Poll the lightweight status endpoint while parsing is in flight; once the
    // job settles, reload the Inertia page to pull in the parsed schedule.
    useEffect(() => {
        if (!isPending) {
            return;
        }

        let active = true;
        const timer = window.setInterval(() => {
            fetch(route('mpp.status', file.id), { headers: { Accept: 'application/json' } })
                .then((response) => (response.ok ? response.json() : null))
                .then((payload: { status?: string } | null) => {
                    if (active && payload && payload.status && payload.status !== file.status) {
                        router.reload({ only: ['file', 'gantt'] });
                    }
                })
                .catch(() => {
                    /* transient — keep polling */
                });
        }, 2500);

        return () => {
            active = false;
            window.clearInterval(timer);
        };
    }, [isPending, file.id, file.status]);

    const chart = useMemo(() => mppToGantt(gantt), [gantt]);

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('mpp.index.title', 'MPP Viewer'), href: '/mpp' },
        { title: file.original_name, href: route('mpp.show', file.id) },
    ];

    const destroy = (): void => {
        if (window.confirm(t('mpp.index.delete_confirm', 'Delete this file? This cannot be undone.'))) {
            router.delete(route('mpp.destroy', file.id));
        }
    };

    const refresh = (): void => {
        router.post(route('mpp.refresh', file.id), {}, { preserveScroll: true });
    };

    const actions = (
        <>
            <Button variant="outline" onClick={() => router.visit(route('mpp.index'))}>
                <ArrowLeft />
                {t('mpp.show.back', 'Back to files')}
            </Button>
            <Button asChild variant="outline">
                <a href={route('mpp.download', file.id)}>
                    <Download />
                    {t('mpp.show.download', 'Download original')}
                </a>
            </Button>
            <Button variant="outline" onClick={refresh}>
                <RefreshCw />
                {t('mpp.show.refresh', 'Keep 30 more days')}
            </Button>
            {canDelete ? (
                <Button variant="outline" onClick={destroy}>
                    <Trash2 />
                    {t('mpp.show.delete', 'Delete')}
                </Button>
            ) : null}
        </>
    );

    const subtitleParts = [file.project_meta?.name, file.format?.toUpperCase()].filter(Boolean) as string[];

    return (
        <>
            <Head title={file.original_name} />
            <IndexLayout breadcrumbs={breadcrumbs} title={file.original_name} subtitle={subtitleParts.join(' · ') || undefined} actions={actions}>
                {file.status === 'completed' && chart.tasks.length > 0 ? (
                    <>
                        <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            <span>
                                {t('mpp.show.meta.tasks', 'Tasks')}:{' '}
                                <span className="text-foreground font-medium">{file.project_meta?.task_count ?? chart.tasks.length}</span>
                            </span>
                            {file.project_meta?.start ? (
                                <span>
                                    {t('mpp.show.meta.start', 'Start')}:{' '}
                                    <span className="text-foreground font-medium">{file.project_meta.start}</span>
                                </span>
                            ) : null}
                            {file.project_meta?.finish ? (
                                <span>
                                    {t('mpp.show.meta.finish', 'Finish')}:{' '}
                                    <span className="text-foreground font-medium">{file.project_meta.finish}</span>
                                </span>
                            ) : null}
                        </div>
                        <div className="bg-card h-[72vh] w-full overflow-hidden rounded-xl border">
                            <MppGanttViewer tasks={chart.tasks} links={chart.links} />
                        </div>
                    </>
                ) : file.status === 'failed' ? (
                    <EmptyState
                        icon={<AlertCircle className="text-danger-subtle-foreground" />}
                        title={t('mpp.show.failed.title', 'Could not read this file')}
                        description={file.error || t('mpp.show.failed.description', 'The file could not be parsed as a supported project format.')}
                    />
                ) : file.status === 'completed' ? (
                    <EmptyState
                        icon={<GanttChartSquare />}
                        title={t('mpp.show.empty.title', 'No tasks found')}
                        description={t('mpp.show.empty.description', 'This project file does not contain any scheduled tasks to display.')}
                    />
                ) : (
                    <EmptyState
                        icon={<Loader2 className="animate-spin" />}
                        title={t('mpp.show.processing.title', 'Parsing the schedule…')}
                        description={t(
                            'mpp.show.processing.description',
                            'This usually takes a few seconds. The chart appears automatically when it is ready.',
                        )}
                    />
                )}
            </IndexLayout>
        </>
    );
}
